<script setup>
import navItems from '@/navigation/vertical'
import NavBarHelp from '@/layouts/components/NavBarHelp.vue'
import NavBarNotifications from '@/layouts/components/NavBarNotifications.vue'
import UserProfile from '@/layouts/components/UserProfile.vue'
import { VerticalNavLayout } from '@layouts'

const breadcrumbs = ref([])

provide('breadcrumbs', breadcrumbs)

const breadcrumbItems = computed(() => breadcrumbs.value?.slice(0, -1).map(item => ({ ...item, disabled: false })))

const mobileBreadcrumbFirstItem = computed(() => breadcrumbs.value?.find(item => item?.title))

const mobileBreadcrumbRestTitle = computed(() => breadcrumbs.value?.slice(1).map(item => item?.title).filter(Boolean).join(' / '))

const router = useRouter()

router.beforeResolve(() => {
  breadcrumbs.value = []
})
</script>

<template>
  <VerticalNavLayout :nav-items="navItems">
    <template #navbar="{ toggleVerticalOverlayNavActive }">
      <div class="d-flex h-100 align-center">
        <IconBtn
          id="vertical-nav-toggle-btn"
          class="ms-n3 d-lg-none"
          @click="toggleVerticalOverlayNavActive(true)"
        >
          <VIcon
            size="26"
            icon="tabler-menu-2"
          />
        </IconBtn>
        <div class="navbar-page-heading d-none d-lg-block">
          <h4 class="navbar-page-title">
            {{ breadcrumbs[breadcrumbs.length - 1]?.title }}
          </h4>
          <VBreadcrumbs
            v-if="breadcrumbItems?.length > 0"
            class="d-none d-lg-flex"
            style="margin-left: -4px; padding: 0"
            :items="breadcrumbItems"
            divider=""
            :item-title="i => (i?.title || '').toUpperCase()"
          >
            <template #divider>
              <VIcon>tabler-chevron-right</VIcon>
            </template>
            <template #title="{item: {title}}">
              <div class="breadcrumb-title">
                {{ title }}
              </div>
            </template>
          </VBreadcrumbs>
        </div>
        <VSpacer />
        <NavBarHelp />
        <NavBarNotifications class="me-2" />
        <UserProfile />
      </div>
    </template>
    <div
      v-if="mobileBreadcrumbFirstItem"
      class="mobile-breadcrumbs d-lg-none"
    >
      <NuxtLink
        v-if="mobileBreadcrumbFirstItem.to"
        :to="mobileBreadcrumbFirstItem.to"
        class="mobile-breadcrumb-link"
      >
        {{ mobileBreadcrumbFirstItem.title }}
      </NuxtLink>
      <span
        v-else
        class="mobile-breadcrumb-link"
      >
        {{ mobileBreadcrumbFirstItem.title }}
      </span>
      <template v-if="mobileBreadcrumbRestTitle">
        <span class="mobile-breadcrumb-divider">/</span>
        <span class="mobile-breadcrumb-current">
          {{ mobileBreadcrumbRestTitle }}
        </span>
      </template>
    </div>
    <slot />
  </VerticalNavLayout>
</template>

<style lang="scss" scoped>
.breadcrumb-title {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  color: black;
  font-size: 12px;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

.navbar-page-heading {
  min-inline-size: 0;
}

.navbar-page-title {
  overflow: hidden;
  margin-block-end: 0;
  text-overflow: ellipsis;
}

.mobile-breadcrumbs {
  overflow: hidden;
  display: flex;
  align-items: center;
  inline-size: 100%;
  max-inline-size: 100%;
  margin-block-end: 4px;
  padding-block: 4px 0;
  padding-inline: 0;
  color: rgb(var(--v-theme-on-background));
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
  white-space: nowrap;
}

.mobile-breadcrumb-link {
  overflow: hidden;
  flex: 0 0 auto;
  max-inline-size: 42%;
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile-breadcrumb-divider {
  flex: 0 0 auto;
  padding-inline: 4px;
}

.mobile-breadcrumb-current {
  overflow: hidden;
  flex: 1 1 auto;
  min-inline-size: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 1279.98px) {
  .navbar-page-heading {
    overflow: hidden;
    max-inline-size: calc(100vw - 176px);
  }

  .navbar-page-title {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-size: 14px;
    line-height: 1.25;
    white-space: normal;
    word-break: break-word;
  }
}
</style>
